import { isEqual } from 'lodash';
import { useCallback, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';
import { FakeLink } from '@hivebrite/design-system/Link';
import { UsersAvatarList } from '@hivebrite/design-system/UsersAvatarList';

import { Comments } from 'hb-react/frontoffice/apps/CommentsV2';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ReactionsComments from 'hb-react/shared/components/DesignSystem/Reactions/ReactionsComments';
import ReactionsLikes from 'hb-react/shared/components/DesignSystem/Reactions/ReactionsLikes';
import ReactionsShares from 'hb-react/shared/components/DesignSystem/Reactions/ReactionsShares';
import UsersModal from 'hb-react/shared/components/DesignSystem/UsersModal';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import IconHeart from 'hb-react/shared/icons/Heart';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';
import { fullName } from 'helpers/name';

import { getMostRecentCommentUsers } from './utils/reactionsUtils';

// We're formatting the commentsUserList to include the maiden name to the user name
// The UsersAvatarList from the DS is displaying the name property without formatting it there
const formatCommentsUsersList = (
  commentsUserList,
  flagEvUserPicturesToGlobalStorage,
) =>
  commentsUserList.map((user) => ({
    ...user,
    name: fullName(user),
    photo_thumb_url: flagEvUserPicturesToGlobalStorage
      ? user.new_photo?.thumbnail_url
      : user.photo_thumb_url,
  }));

const Reactions = ({
  comments = [],
  displayCommentsByDefault,
  options,
  handleShareButton,
  id,
  initialLikes = [],
  initialTotalComments,
  isLikeModalFocusOnRef,
  isOpenInModal = false,
  lastContributors: lastCommentsUserList,
  measure,
  onCommentCallback,
  onLikeCallback,
  onNewCommentCallback,
  onTogglePostLike,
  onToggleComments,
  parentUrl,
  railsType,
  setDisplayComments: SetExternalDisplayComments,
  sharedContent,
  sharedFrom,
  sharedUrl,
  shareToLiveFeedEnabled,
  shareToLiveFeedUrl,
  shareUrl,
  showDivider = true,
  type,
}) => {
  const { currentUser } = useDefaultAppContexts();

  const { flagEvUserPicturesToGlobalStorage } = useFeatureFlag();

  const [displayComments, setDisplayComments] = useState(
    displayCommentsByDefault || false,
  );
  // TODO: remove with flag-uj-anchoring-comments
  const [commentFocus, setCommentFocus] = useState(false);
  const [likes, setLikes] = useState(initialLikes);

  const updateDisplayComments =
    SetExternalDisplayComments ?? setDisplayComments;

  const [totalComments, setTotalComments] = useState(initialTotalComments);

  const [updatedCommentsList, setUpdatedCommentsList] = useState(comments);

  const [commentsUserList, setCommentsUserList] = useState(
    lastCommentsUserList || [],
  );

  const commentsRef = useRef(null);

  useEffect(() => {
    if (updatedCommentsList.length > 0) {
      setCommentsUserList((prevCommentsUsers) =>
        getMostRecentCommentUsers(prevCommentsUsers, updatedCommentsList),
      );
    }
  }, [updatedCommentsList]);

  const [likesModalIsOpen, setLikesModalIsOpen] = useState(false);

  const likesCount = likes?.count || 0;
  const hasLikes = likesCount > 0;
  const hasComments = totalComments > 0;

  const justifyContent = () => (hasComments ? 'flex-end' : 'flex-start');

  const handleUpdateComments = useCallback(
    ({ comments: newComments, totalComments: newTotalComments }) => {
      if (!isEqual(updatedCommentsList, newComments)) {
        setUpdatedCommentsList(newComments);
        setTotalComments(newTotalComments);

        onCommentCallback?.(newTotalComments, newComments);
      }
    },
    [onCommentCallback, updatedCommentsList],
  );

  const handleDisplayComments = useCallback(
    (e) => {
      e.preventDefault();
      updateDisplayComments(true);
      setCommentFocus(true);
      // Comments V3
      commentsRef.current?.focusCommentForm?.();
      onToggleComments?.(true);
    },
    [updateDisplayComments, setCommentFocus, onToggleComments],
  );

  const handleDisplayCommentsAndFocus = useCallback(
    (e) => {
      e.preventDefault();
      updateDisplayComments(true);
      setCommentFocus(true);
      // Comments V3
      commentsRef.current?.focusCommentForm?.();
      onToggleComments?.(true);
    },
    [updateDisplayComments, setCommentFocus, onToggleComments],
  );

  const handleOpenLikesModal = useCallback((e) => {
    e.preventDefault();
    setLikesModalIsOpen(true);
  }, []);

  const handleCloseLikesModal = useCallback(
    () => setLikesModalIsOpen(false),
    [],
  );

  // TODO: remove with flag-uj-anchoring-comments
  const handleCommentsOnBlur = useCallback(() => {
    setCommentFocus(false);
  }, []);

  const commentsCountLabel = n__(
    '%{comments} comment',
    '%{comments} comments',
    totalComments,
  ).i__({
    comments: totalComments,
  });

  useEffect(() => {
    try {
      measure?.();
      // eslint-disable-next-line no-empty
    } catch (_err) {}
  }, [measure, displayComments]);

  return (
    <>
      {(hasLikes || hasComments) && (
        <SocialWrapper
          alignItems="center"
          justifyContent={
            hasLikes && hasComments ? 'space-between' : justifyContent()
          }
        >
          {hasLikes && (
            <FakeLink
              tabIndex={0}
              data-testid="likes-count-wrapper"
              onClick={handleOpenLikesModal}
              onKeyDown={onEnterOrSpacePress(handleOpenLikesModal)}
              alignItems="center"
            >
              <Box mr={2} color="primary">
                <StyledIconHeart />
              </Box>
              {n__('%{likes} Like', '%{likes} Likes', likesCount).i__({
                likes: likesCount,
              })}
            </FakeLink>
          )}

          {hasComments && (
            <Box alignItems="center">
              <UsersAvatarList
                ariaExpanded={displayComments}
                imgSize={36}
                maxAvatars={3}
                onClickMore={handleDisplayComments}
                screenReadersMoreButtonText={s__(
                  'Accessibility|Display the comments section',
                )}
                users={formatCommentsUsersList(
                  commentsUserList,
                  flagEvUserPicturesToGlobalStorage,
                )}
              >
                {commentsCountLabel}
              </UsersAvatarList>
            </Box>
          )}
        </SocialWrapper>
      )}

      {(showDivider || hasLikes || hasComments) && <Divider mt={6} mb={3} />}

      <ReactionsWrapper>
        <ReactionsBlock>
          <ReactionsLikes
            initialLikes={likes}
            onTogglePostLike={onTogglePostLike}
            type={type}
            onLikeCallback={onLikeCallback}
            setLikes={setLikes}
            showFullText
          />
        </ReactionsBlock>
        <ReactionsBlock>
          <ReactionsComments
            showFullText
            onClick={handleDisplayCommentsAndFocus}
            dataTestId="open-comments-action"
            ariaExpanded={displayComments}
          />
        </ReactionsBlock>
        {handleShareButton || shareToLiveFeedEnabled ? (
          <ReactionsBlock>
            <ReactionsShares
              content={sharedContent}
              onClick={handleShareButton}
              shareToLiveFeedEnabled={shareToLiveFeedEnabled}
              shareToLiveFeedUrl={shareToLiveFeedUrl}
              shareUrl={shareUrl}
              sharedFrom={sharedFrom}
              sharedUrl={sharedUrl}
            />
          </ReactionsBlock>
        ) : null}
      </ReactionsWrapper>

      {likesModalIsOpen && (
        <UsersModal
          onCloseModal={handleCloseLikesModal}
          routes={{
            fetchUsers: Routes.user_likes_path(currentUser.id, {
              likeable_id: id,
              likeable_type: railsType,
            }),
          }}
          isFocusOnRef={isLikeModalFocusOnRef}
          title={I18n.t('web.social_header.users_who_like_it')}
          screenReadersCloseButtonText={s__(
            'LikesAccessibility|Close the users who like it list window',
          )}
          userNameFormat={fullName}
        />
      )}

      {displayComments !== false && (
        <CommentsWrapper>
          <Comments
            commentsRef={commentsRef}
            // TODO: remove with flag-uj-anchoring-comments
            autoFocus={commentFocus}
            commentableId={id}
            commentableType={railsType}
            // TODO: remove with flag-uj-anchoring-comments
            comments={updatedCommentsList}
            options={options}
            isOpenInModal={isOpenInModal}
            parentUrl={parentUrl}
            // TODO: remove with flag-uj-anchoring-comments
            onBlur={handleCommentsOnBlur}
            onCommentsUpdate={handleUpdateComments}
            onNewCommentCallback={onNewCommentCallback}
            // TODO: remove with flag-uj-anchoring-comments
            totalComments={totalComments}
          />
        </CommentsWrapper>
      )}
    </>
  );
};

export default Reactions;

const ReactionsWrapper = styled(Box)`
  position: relative;
`;

const ReactionsBlock = styled(Box).attrs(() => ({
  ml: -1,
  mr: 3,
}))``;

const CommentsWrapper = styled(Box).attrs(() => ({
  display: 'block',
  mt: 3,
  mb: -3,
}))`
  .bordered--top {
    border-top: unset;
  }
`;

const SocialWrapper = styled(Box).attrs(() => ({
  mt: 4,
  mb: -2,
}))`
  min-height: 36px;
`;

const StyledIconHeart = styled(IconHeart)`
  height: ${({ theme }) => theme.fontSizes[3]};
  width: ${({ theme }) => theme.fontSizes[3]};
`;
